import React, { useState, useEffect } from 'react';
import { Button, Text, Progress, Group, Stack } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faCheckCircle, 
  faExclamationTriangle,
  faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import { SoundtrackService, SoundtrackStatus } from '../../../services/soundtrackService';
import { useNotification } from '../../../context/NotificationContext';
import { downloadVideo } from '../../../services/autoDownloadService';
import { useAuth } from '../../../context/Auth';

interface SoundtrackDownloadSectionProps {
  projectId: string;
  jobId?: string;
  onStatusUpdate?: (status: SoundtrackStatus) => void;
}

export const SoundtrackDownloadSection: React.FC<SoundtrackDownloadSectionProps> = ({
  projectId,
  jobId,
  onStatusUpdate
}) => {
  const [status, setStatus] = useState<SoundtrackStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const { showNotification } = useNotification();
  const soundtrackService = new SoundtrackService();
  const { token, user } = useAuth();
  
  // Check soundtrack status on mount and when projectId changes (but don't auto-start polling)
  useEffect(() => {
    if (projectId) {
      checkSoundtrackStatus();
    }
  }, [projectId]);

  // Start polling when jobId is provided from parent (user-triggered export)
  useEffect(() => {
    if (jobId && !isPolling) {
      startPolling();
    }
  }, [jobId]);

  const checkSoundtrackStatus = async () => {
    try {
      const soundtrackStatus = await soundtrackService.getSoundtrackStatus(projectId);
      setStatus(soundtrackStatus);
      
      if (onStatusUpdate) {
        onStatusUpdate(soundtrackStatus);
      }
      
      // Don't auto-start polling on page load - only show completed/failed status
      
      return soundtrackStatus;
    } catch (error) {
      console.error('Error checking soundtrack status:', error);
      return null;
    }
  };

  const startPolling = async () => {
    setIsPolling(true);
    let pollCount = 0;
    const maxPolls = 20; // 20 polls * 3 seconds = 1 minute timeout (shorter)
    const startTime = Date.now();
    
    const poll = async () => {
      try {
        pollCount++;
        const currentStatus = await checkSoundtrackStatus();
        
        if (currentStatus?.status === 'completed') {
          setIsPolling(false);
          // showNotification({
          //   title: 'Video Ready!',
          //   message: 'Your video with soundtrack is ready!',
          //   type: 'success'
          // });
          
          // Don't auto-open here - SoundtrackExportButton handles auto-opening
          // User can manually download if needed using the download button
          
          return;
        } else if (currentStatus?.status === 'failed') {
          setIsPolling(false);
          showNotification({
            title: 'Export Failed',
            message: 'Failed to create video with soundtrack. Please try again.',
            type: 'error'
          });
          return;
        } else if (pollCount >= maxPolls) {
          // Timeout after 1 minute - assume backend failed
          setIsPolling(false);
          showNotification({
            title: 'Export Timeout',
            message: 'Export is taking too long. There may be a backend issue. Please try again.',
            type: 'error'
          });
          
          // Reset button state
          if (onStatusUpdate) {
            onStatusUpdate({ hasSoundtrack: false, status: 'failed' });
          }
          return;
        }
        
        // Continue polling if still processing
        if (currentStatus?.status === 'processing') {
          setTimeout(poll, 3000); // Poll every 3 seconds
        } else {
          setIsPolling(false);
        }
        
      } catch (error) {
        console.error('Polling error:', error);
        pollCount++;
        
        if (pollCount >= maxPolls) {
          setIsPolling(false);
          showNotification({
            title: 'Export Error',
            message: 'Unable to check export status. Backend may be down. Please try again.',
            type: 'error'
          });
          
          // Reset button state
          if (onStatusUpdate) {
            onStatusUpdate({ hasSoundtrack: false, status: 'failed' });
          }
          return;
        }
        
        setTimeout(poll, 5000); // Retry with longer delay on error
      }
    };
    
    poll();
  };

  const handleDownload = async () => {
    if (status?.soundtrackVideoUrl) {
      try {
        const filename = `soundtrack-video-${Date.now()}.mp4`;
        
        console.log('🎬 [SOUNDTRACK] Starting video download...');
        console.log('URL:', status.soundtrackVideoUrl);
        
        // Use handleDownload which automatically handles S3 URLs through proxy
        await downloadVideo(status.soundtrackVideoUrl, {
          filename,
          onStart: () => {
            console.log('📥 [SOUNDTRACK] Download started...');
          },
          onComplete: () => {
            console.log('✅ [SOUNDTRACK] Video downloaded successfully');
            showNotification({
              title: 'Download Complete',
              message: 'Your video with soundtrack has been downloaded',
              type: 'success'
            });
          },
          onError: (error) => {
            console.error('❌ [SOUNDTRACK] Failed to download video:', error);
            showNotification({
              title: 'Download Failed',
              message: 'Failed to download video. Please try again.',
              type: 'error'
            });
          }
        });
      } catch (error) {
        console.error('❌ [SOUNDTRACK] Download error:', error);
        showNotification({
          title: 'Download Failed',
          message: 'An error occurred while downloading. Please try again.',
          type: 'error'
        });
      }
    }
  };

  const renderStatusIndicator = () => {
    if (!status || status.status === 'none') {
      return null;
    }

    switch (status.status) {
      case 'processing':
        // Don't show processing status here - it's now handled by the button
        return null;

      case 'completed':
        return (
          <div style={{ 
            marginLeft: '12px',
            display: 'inline-flex',
            alignItems: 'center'
          }}>
            <div
              onClick={handleDownload}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#10b981';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="Download Video with Soundtrack"
            >
              <FontAwesomeIcon 
                icon={faDownload} 
                style={{ 
                  color: 'white',
                  fontSize: '18px'
                }}
              />
            </div>
          </div>
        );

      case 'failed':
        return (
          <Group gap="sm" style={{ marginTop: '16px' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444' }} />
            <Text size="sm" style={{ color: '#ef4444' }}>
              Failed to create video with soundtrack
            </Text>
          </Group>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {renderStatusIndicator()}
    </div>
  );
};
